/**
 * Display helpers for `LiveTrackAction` records in the History Inspector.
 *
 * `LiveTrackAction` has no single "type" column — the action performed is
 * implied by which mutation field is populated. These helpers derive the
 * display label + a status tone so the inspector card stays declarative.
 */

/** True when an `update_fields` / `live_track_backup`-style value carries real content. */
function hasContent(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed !== '' && trimmed !== '[]' && trimmed !== '{}' && trimmed !== 'null';
  }
  return true;
}

/**
 * The subset of a `LiveTrackAction` the label derivation needs. `update_fields`
 * is intentionally `unknown` — Elasticsearch may hand it back as a JSON string
 * rather than the model's cast array.
 */
type ActionLabelFields = {
  rollback_to_action_id?: number | null;
  update_audio_file?: string | null;
  update_cover_art?: string | null;
  update_fields?: unknown;
  source?: string | null;
};

/**
 * Human label for what an action did. Priority order matches the pipeline:
 * a rollback supersedes any update; an audio-file update is the headline over
 * a cover-art / field update when several are set on one action.
 */
export function deriveActionLabel(action: ActionLabelFields): string {
  if (action.rollback_to_action_id) {
    return 'Rollback';
  }
  if (action.update_audio_file) {
    return 'Audio file update';
  }
  if (action.update_cover_art) {
    return 'Cover art update';
  }
  if (hasContent(action.update_fields)) {
    return 'Field update';
  }
  return action.source || 'Action';
}

/**
 * `live_track_action_status_id` -> label, mirroring the static
 * `App\Models\LiveTrackActionStatus` table. The statuses are a fixed enum
 * (`HasStaticEntries`), so a local map is the frontend equivalent of the model.
 */
const STATUS_LABELS: Record<number, string> = {
  1: 'Unprocessed',
  2: 'In process',
  3: 'Success',
  4: 'File update error',
  5: 'Cover art update error',
  6: 'Metadata update error',
  7: 'Rollback failure',
};

/** Human label for a `live_track_action_status_id`. */
export function statusLabelForId(statusId: number | null | undefined): string {
  return (statusId != null ? STATUS_LABELS[statusId] : undefined) ?? 'Unknown';
}

export type ActionStatusTone = 'success' | 'error' | 'pending' | 'neutral';

/**
 * Tone for a `live_track_action_status_id` (see `App\Models\LiveTrackActionStatus`):
 * 3 = Success, 4-7 = error states, 2 = In process, 1 = Unprocessed.
 */
export function actionStatusTone(statusId: number | null | undefined): ActionStatusTone {
  switch (statusId) {
    case 3:
      return 'success';
    case 4:
    case 5:
    case 6:
    case 7:
      return 'error';
    case 2:
      return 'pending';
    default:
      return 'neutral';
  }
}
